import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfigurableDrawer from "./ConfigurableDrawer";
import { Badge, Btn, dt, ContactActivityPreview } from "./common";
import { toast } from "sonner";
import { Mail, Phone, MessageSquare, Building2, ExternalLink, Copy, UserPlus, Users, Filter, Check, Save, PhoneCall } from "lucide-react";
import SmsComposer from "./SmsComposer";
import { WebphoneDialog } from "./WebphoneDialog";
import { api, formatApiErrorDetail } from "../api";

// Formata data curta (dd mmm aaaa)
function _dtShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

// ---------------------------------------------------------------------------
// Bloco de listas de marketing (Iter106)
// ---------------------------------------------------------------------------
function MarketingListsBlock({ contactId }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = () => {
    if (!contactId) return;
    api.get(`/crm/contacts/${contactId}/marketing-lists`).then((r) => {
      setData(r.data);
      setSelected(new Set((r.data?.lists || []).filter((l) => l.subscribed).map((l) => l.id)));
    }).catch(() => setData({ lists: [], segments: [], email: "" }));
  };
  useEffect(load, [contactId]);

  const toggle = (lid) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(lid)) s.delete(lid); else s.add(lid);
      return s;
    });
  };

  const dirty = data ? (() => {
    const cur = new Set((data.lists || []).filter((l) => l.subscribed).map((l) => l.id));
    if (cur.size !== selected.size) return true;
    for (const x of cur) if (!selected.has(x)) return true;
    return false;
  })() : false;

  const save = async () => {
    if (!contactId || !data) return;
    setSaving(true);
    try {
      const r = await api.put(`/crm/contacts/${contactId}/marketing-lists`, { list_ids: Array.from(selected) });
      const added = r.data?.added?.length || 0;
      toast.success(added ? `${added} lista(s) adicionada(s). Total: ${selected.size}.` : `Subscricoes actualizadas. Total: ${selected.size}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nao foi possivel guardar.");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-[12px] text-muted2 py-2">A carregar listas...</div>;
  if (!data.email) return <div className="text-[12px] text-amber-600 py-2">O contacto nao tem email. Adicione um email para o poder subscrever a listas de marketing.</div>;

  return (
    <div className="space-y-3" data-testid="contact-marketing-lists">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-muted2">
          Email marketing: <b className="text-navy">{data.email}</b>
          {data.marketing_contact_id ? (
            <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded ${data.estado_marketing === "unsubscribed" ? "bg-red-100 text-red-700" : data.estado_marketing === "bounced" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {(data.estado_marketing || "ativo").toUpperCase()}
            </span>
          ) : (
            <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">SEM REGISTO</span>
          )}
        </div>
        <button onClick={() => nav("/app/marketing")}
          data-testid="open-marketing"
          className="text-[11px] font-semibold text-blue hover:underline inline-flex items-center gap-1">
          <ExternalLink size={11} /> Marketing
        </button>
      </div>

      {/* Listas */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted2 mb-1 flex items-center gap-1.5">
          <Users size={11} /> Listas
        </div>
        {(data.lists || []).length === 0 && (
          <div className="text-[12px] text-muted2 py-1">Sem listas criadas. Crie em /app/marketing.</div>
        )}
        <div className="space-y-1">
          {(data.lists || []).map((l) => {
            const isSel = selected.has(l.id);
            return (
              <label key={l.id} data-testid={`ml-toggle-${l.id}`}
                className={`flex items-start gap-2 px-2 py-1.5 rounded border cursor-pointer transition-colors ${isSel ? "bg-emerald-50 border-emerald-200" : "bg-white border-tint hover:bg-slate-50"}`}>
                <input type="checkbox" checked={isSel} onChange={() => toggle(l.id)} className="mt-0.5 accent-emerald-600" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[12px] font-semibold truncate ${isSel ? "text-emerald-700" : "text-navy"}`}>{l.nome}</span>
                    {l.plataforma && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-slate-100 text-slate-600 uppercase">{l.plataforma}</span>}
                    <span className="text-[10px] text-slate-400 ml-auto whitespace-nowrap">{l.n_contactos || 0} contactos</span>
                  </div>
                  {l.descricao && <div className="text-[10px] text-slate-500 truncate">{l.descricao}</div>}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Segmentos (read-only, dinamicos) */}
      {(data.segments || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted2 mb-1 flex items-center gap-1.5">
            <Filter size={11} /> Segmentos (dinamicos)
          </div>
          <div className="flex flex-wrap gap-1">
            {(data.segments || []).map((s) => (
              <span key={s.id} title={s.descricao}
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${s.match ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-tint"}`}>
                {s.match && <Check size={9} />} {s.nome}
              </span>
            ))}
          </div>
          <div className="text-[10px] text-muted2 mt-1">Regras avaliadas no momento; nao editaveis daqui.</div>
        </div>
      )}

      {/* Guardar */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-tint">
        {dirty && <span className="text-[11px] text-amber-600 font-semibold">Alteracoes por guardar</span>}
        <button onClick={save} disabled={!dirty || saving}
          data-testid="save-marketing-lists"
          className={`inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded ${dirty && !saving ? "bg-blue text-white hover:bg-blue/90" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}>
          <Save size={12} /> {saving ? "A guardar..." : "Guardar subscricoes"}
        </button>
      </div>
    </div>
  );
}

const FIELD_DEFS = [
  { key: "nome", label: "Nome", render: (c) => <span className="font-semibold text-navy">{c.nome || "-"}</span> },
  { key: "cargo", label: "Cargo", render: (c) => c.cargo || "-" },
  { key: "departamento", label: "Departamento", render: (c) => c.departamento || "-" },
  { key: "emails", label: "Email(s)", render: (c) => (
    (c.emails || []).length > 0
      ? <div className="space-y-0.5">{(c.emails || []).map((e, i) => (
          <a key={i} href={`mailto:${e}`} className="block text-blue hover:underline text-[12px] truncate">{e}</a>
        ))}</div>
      : "-"
  ) },
  { key: "telefones", label: "Telefone(s)", render: (c) => (
    (c.telefones || []).length > 0
      ? <div className="space-y-0.5">{(c.telefones || []).map((t, i) => (
          <a key={i} href={`tel:${t}`} className="block text-navy hover:text-blue text-[12px]">{t}</a>
        ))}</div>
      : "-"
  ) },
  { key: "telemovel", label: "Telemovel", render: (c) => c.telemovel || "-" },
  { key: "ciclo_vida", label: "Ciclo de vida", render: (c) => <Badge value={c.ciclo_vida || "-"} /> },
  { key: "linkedin", label: "LinkedIn", render: (c) => c.linkedin ? <a href={c.linkedin} target="_blank" rel="noreferrer" className="text-blue hover:underline">Perfil</a> : "-" },
  { key: "account_id", label: "Conta CRM", render: (c) => (
    c.account_id ? (
      <a href={`/app/crm/contas/${c.account_id}`}
        className="inline-flex items-center gap-1 text-blue hover:underline font-medium"
        data-testid="contact-drawer-account-link">
        <Building2 size={11} /> {c.account_nome || "vinculada"}
        {c.account_tipo && <span className="text-[9px] uppercase text-slate-400 ml-1">{c.account_tipo}</span>}
      </a>
    ) : <span className="text-slate-400 italic text-[11px]">sem conta</span>
  ) },
  { key: "notas", label: "Notas", render: (c) => c.notas || "-" },
  { key: "criado_em", label: "Criado", render: (c) => (
    <div className="text-[11px] leading-tight">
      {c.criado_em ? dt(c.criado_em) : "-"}
      {(c.criado_por_nome || c.criado_por_email || c.origem) && (
        <div className="text-slate-500 mt-0.5" data-testid="contact-drawer-creator">
          {c.origem && <span>Origem: <b className="text-navy">{c.origem}</b></span>}
          {(c.criado_por_nome || c.criado_por_email) && (
            <span className="ml-1">por <b className="text-navy">{c.criado_por_nome || c.criado_por_email}</b></span>
          )}
        </div>
      )}
    </div>
  ) },
  { key: "letscall_contact_id", label: "LetsCall One", render: (c) => (
    c.letscall_contact_id ? (
      <div className="flex items-center gap-1.5 text-[11px]" data-testid="contact-letscall-sync-indicator">
        <PhoneCall size={11} className="text-emerald-600" />
        <span className="text-emerald-700 font-semibold">Sincronizado</span>
        {c.letscall_synced_at && (
          <span className="text-slate-400">em {_dtShort(c.letscall_synced_at)}</span>
        )}
      </div>
    ) : (
      <span className="text-slate-400 italic text-[11px]">Nao sincronizado</span>
    )
  ) },
];

const DEFAULT_ACTIONS = ["email", "sms", "ligar", "ligar_letscall", "sync_letscall", "copiar_email", "abrir_conta", "editar", "nova_atividade"];
const DEFAULT_ACTIONS_VISIBLE = { email: true, sms: true, ligar: true, ligar_letscall: false, sync_letscall: false, copiar_email: true, abrir_conta: true, editar: true, nova_atividade: false };

function makeActionDefs(nav, openSms, openEdit, onLigar, onSyncLetsCall) {
  const _emailNav = (c) => {
    const e = (c.emails || [])[0];
    if (!e) return;
    if (c.account_id) {
      nav(`/app/crm/contas/${c.account_id}?tab=emails&compose=1&email_to=${encodeURIComponent(e)}`);
    } else {
      nav(`/app/crm/atividades?contact_id=${c.id}&nova=1&tipo=email`);
    }
  };
  return [
    { key: "email", label: "Enviar email", icon: Mail,
      enabled: (c) => (c.emails || []).length > 0,
      run: _emailNav },
    { key: "sms", label: "Enviar SMS", icon: MessageSquare,
      enabled: (c) => !!c.telemovel || (c.telefones || []).some((t) => /^(?:\+?351)?9\d{8}$/.test(String(t).replace(/\s+/g, ""))),
      run: (c) => openSms(c) },
    { key: "ligar", label: "Ligar (LetsCall)", icon: PhoneCall,
      enabled: (c) => (c.telefones || []).length > 0 || !!c.telemovel,
      run: (c) => { const t = c.telemovel || (c.telefones || [])[0]; if (t) onLigar(t); } },
    { key: "ligar_letscall", label: "Click-to-Call (LetsCall)", icon: PhoneCall,
      enabled: (c) => (c.telefones || []).length > 0 || !!c.telemovel,
      run: (c) => { const t = c.telemovel || (c.telefones || [])[0]; if (t) onLigar(t); } },
    { key: "sync_letscall", label: "Sincronizar para LetsCall", icon: Users,
      enabled: (c) => ((c.telefones || []).length > 0 || !!c.telemovel),
      run: (c) => onSyncLetsCall(c) },
    { key: "copiar_email", label: "Copiar email", icon: Copy,
      enabled: (c) => (c.emails || []).length > 0,
      run: async (c) => {
        const e = (c.emails || [])[0]; if (!e) return;
        try { await navigator.clipboard.writeText(e); toast.success(`${e} copiado.`); }
        catch { toast.error("Nao foi possivel copiar."); }
      } },
    { key: "abrir_conta", label: "Abrir conta", icon: Building2,
      enabled: (c) => !!c.account_id,
      run: (c) => nav(`/app/crm/contas/${c.account_id}`) },
    { key: "editar", label: "Editar contacto", icon: Filter,
      enabled: (c) => !!c.id,
      run: (c) => nav(`/app/crm/contactos?edit_id=${c.id}`) },
    { key: "nova_atividade", label: "Nova atividade", icon: UserPlus,
      enabled: (c) => !!c.id,
      run: (c) => nav(`/app/crm/atividades?contact_id=${c.id}&nova=1`) },
  ];
}

export default function ContactDrawer({ contact, open, onClose }) {
  const nav = useNavigate();
  const [c, setC] = useState(contact || null);
  const [smsFor, setSmsFor] = useState(null);
  const [phoneNum, setPhoneNum] = useState("");
  useEffect(() => { if (open && contact) setC(contact); }, [open, contact]);
  const entity = c || {};
  const placeholders = {
    id: entity.id || "", nome: entity.nome || "", cargo: entity.cargo || "",
    departamento: entity.departamento || "", telemovel: entity.telemovel || "",
    email: (entity.emails || [])[0] || "", telefone: (entity.telefones || [])[0] || "",
    account_id: entity.account_id || "", account_nome: entity.account_nome || "",
    ciclo_vida: entity.ciclo_vida || "", linkedin: entity.linkedin || "",
  };

  const handleSyncLetsCall = async (contact) => {
    try {
      const telefone = contact.telemovel || (contact.telefones || [])[0] || "";
      if (!telefone) return toast.warning("Contacto sem telefone.");
      const { data: result } = await api.post("/channels/letscall/sync-contact", {
        name: contact.nome || "Contacto",
        phone: telefone,
        email: (contact.emails || [])[0] || "",
        external_id: contact.id || "",
      });
      toast.success(`${contact.nome || "Contacto"} sincronizado para LetsCall One.`);
      // Actualiza indicador localmente (campo letscall_contact_id)
      const lcId = result?.letscall_contact_id || result?.id || "synced";
      const now = new Date().toISOString();
      setC((prev) => prev ? { ...prev, letscall_contact_id: String(lcId), letscall_synced_at: now } : prev);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao sincronizar.");
    }
  };

  return (
    <>
    <ConfigurableDrawer
      open={open} onClose={onClose}
      overline="CRM"
      title={entity.nome || "Contacto"}
      testid="contact-drawer"
      uiPrefKey="contact_drawer" uiPrefVersion="v3"
      fieldDefs={FIELD_DEFS}
      actionDefs={makeActionDefs(
        nav,
        (ct) => setSmsFor(ct),
        () => {},
        (tel) => setPhoneNum(tel),
        handleSyncLetsCall
      )}
      defaultActions={DEFAULT_ACTIONS}
      defaultActionsVisible={DEFAULT_ACTIONS_VISIBLE}
      extraSections={[
        {
          key: "marketing_lists",
          label: "Marketing",
          defaultVisible: true,
          render: (ct) => <MarketingListsBlock contactId={ct.id} />,
        },
        {
          key: "activity",
          label: "Atividade recente",
          defaultVisible: true,
          render: (ct) => <ContactActivityPreview contactId={ct.id} />,
        },
      ]}
      entity={entity}
      placeholders={placeholders}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} testid="contact-drawer-close-btn">Fechar</Btn>
          {entity.account_id && <Btn onClick={() => nav(`/app/crm/contas/${entity.account_id}`)} testid="contact-drawer-open-account"><ExternalLink size={14} /> Abrir conta</Btn>}
        </>
      }
    />
    {smsFor && (
      <SmsComposer
        open={!!smsFor}
        onClose={() => setSmsFor(null)}
        contactos={[smsFor]}
        accountId={smsFor.account_id || ""}
        nome={smsFor.nome || ""}
        onSent={() => setSmsFor(null)}
      />
    )}
    <WebphoneDialog open={!!phoneNum} number={phoneNum} onClose={() => setPhoneNum("")} />
    </>
  );
}
